import dayjs from "dayjs";
import { useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  CoachFunction,
  FunctionCoach,
} from "../../../../../../../../api/services/program/models";
import {
  MuiDateFieldFormController,
  MuiGrid,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import {
  TableActionType,
  TableActions,
} from "../../../../../../../../helpers/types/common";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { CoachAssignmentWrapper } from "./coachAssignment.style";
import { AddCoachAssignmentFunction } from "./components/addCoachAssignmentFunction/addCoachAssignmetFunction";
import { CoachAssignmentList } from "./components/coachAssignmentList/coachAssignmentList";
import { CoachAssignmentFormKeys } from "./constants";
import { CoachAssignmentDetails, CoachAssignmentDetailsList } from "./model";
import { coachAssignmentSiteCoreKeys } from "./SiteCoreKeys";

const {
  coachAssignmentToBeAssignedDate,
  coachAssignment,
  coachAssignmentDeleteConfirm,
} = coachAssignmentSiteCoreKeys;

interface CoachAssignmentProps {
  pageId: string;
  dateFormat: string;
  isTMIndividualProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
  onParentSubmitPressed?: boolean;
}

export const CoachAssignment = ({
  pageId,
  dateFormat,
  isTMIndividualProgram,
  isTMLeaderCoachingProgram,
  onParentSubmitPressed,
}: CoachAssignmentProps) => {
  const [state, setState] = useState<{
    showDeleteConfirmModal: boolean;
    showEditModal: boolean;
  }>({
    showDeleteConfirmModal: false,
    showEditModal: false,
  });
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { commonLabels, useFetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { control, setValue, getValues } = useFormContext();
  const getLabel = useFetchLabel(coachAssignment);
  const toDay = dayjs();
  const selectedCoachAssignmentForAction = useRef<CoachAssignmentDetails>();
  const errorWarningMessage = getLabel(coachAssignmentDeleteConfirm);

  const handleActionClick = (
    action: TableActionType,
    selectedCoachAssignment: CoachAssignmentDetails,
  ) => {
    selectedCoachAssignmentForAction.current = selectedCoachAssignment;
    if (action === TableActions.delete) {
      setState((preState) => ({ ...preState, showDeleteConfirmModal: true }));
    } else if (action === TableActions.edit) {
      setState((preState) => ({ ...preState, showEditModal: true }));
    }
  };

  const coachAssignedList = (useWatch({
    control,
    name: CoachAssignmentFormKeys.coachAssignments,
  }) ?? []) as CoachAssignmentDetailsList;

  const setCoachAssignmentList = (value: CoachAssignmentDetailsList) => {
    setValue(CoachAssignmentFormKeys.coachAssignments, value, {
      shouldDirty: true,
    });
  };
  const deleteCoachAssignment = () => {
    if (selectedCoachAssignmentForAction.current) {
      const coachAssignmentData = [...coachAssignedList];
      const index = coachAssignmentData.indexOf(
        selectedCoachAssignmentForAction.current,
      );
      if (index !== -1) {
        coachAssignmentData.splice(index, 1);
        setCoachAssignmentList(coachAssignmentData);
      }
    }
  };

  const handleDeleteClick = () => {
    deleteCoachAssignment();
    setState((preState) => ({ ...preState, showDeleteConfirmModal: false }));
  };

  const handleAddFunction = (
    selectedFunction: CoachFunction,
    selectedCoach?: FunctionCoach,
  ) => {
    setCoachAssignmentList([
      ...coachAssignedList,
      {
        assignmentDate: selectedCoach ? toDay.format() : "",
        coachId: selectedCoach?.id ?? "",
        coachFirstName: selectedCoach?.firstName ?? "",
        coachLastName: selectedCoach?.lastName ?? "",
        functionIdentifier: selectedFunction.identifier,
        functionName: selectedFunction.name,
        functionId: selectedFunction.id,
      },
    ]);
  };

  const hasAllFunctionAssignCoach: boolean = useMemo(() => {
    if (!coachAssignedList?.length) return false;
    return coachAssignedList.every(
      (coachAssignment) => coachAssignment.coachId,
    );
  }, [coachAssignedList]);

  const handleDeleteWarningModalClose = () =>
    setState((preState) => ({ ...preState, showDeleteConfirmModal: false }));

  return (
    <CoachAssignmentWrapper>
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDateFieldFormController
            control={control}
            disabled={hasAllFunctionAssignCoach}
            name={CoachAssignmentFormKeys.toBeAssignDate}
            label={getLabel(coachAssignmentToBeAssignedDate)}
            valueFormat={dateFormat}
            format={dateFormat}
            minDate={toDay}
            value={
              getValues(CoachAssignmentFormKeys.toBeAssignDate) !== ""
                ? dayjs(
                    getValues(CoachAssignmentFormKeys.toBeAssignDate),
                    dateFormat,
                  )
                : null
            }
            id={`${pageId}-${CoachAssignmentFormKeys.toBeAssignDate}`}
            clearable
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>
      </MuiGrid>
      <AddCoachAssignmentFunction
        coachAssignList={coachAssignedList}
        onAddFunctionCallback={handleAddFunction}
        isTMIndividualProgram={isTMIndividualProgram}
        isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
        onParentSubmitPressed={onParentSubmitPressed}
      />

      <CoachAssignmentList
        coachAssignmentList={coachAssignedList}
        onActionClick={handleActionClick}
        dateFormat={dateFormat}
      />
      {state.showDeleteConfirmModal && (
        <WarningConfirmationModal
          show={state.showDeleteConfirmModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleDeleteWarningModalClose}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={errorWarningMessage}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </CoachAssignmentWrapper>
  );
};
